import { useCallback, useState } from 'react'
import { returnOrderBy } from '~/pages/backoffice/sorter'

/** Taille de page des tableaux du back-office. */
export const DEFAULT_PAGE_SIZE = 10

export type SortOrder = 'ascend' | 'descend'

export type SortedInfo = {
  field: string
  order: SortOrder
}

/**
 * État de pagination et de tri partagé par les tableaux paginés du back-office
 * (utilisateurs, statistiques), avec le bloc `skip`/`take`/`orderBy` déjà
 * calculé pour les inputs GraphQL.
 *
 * Le calcul de `skip` vit ici et nulle part ailleurs : dupliqué, il finissait
 * par utiliser une taille de page en dur là où `take` utilisait la variable, et
 * la pagination aurait sauté le jour où la taille devient réglable.
 */
export function useTableQuery(pageSize: number = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState<number>(1)
  const [sortedInfo, setSortedInfo] = useState<SortedInfo | undefined>()

  const handleSort = (field: string) => {
    setSortedInfo({
      field,
      order: sortedInfo?.order === 'ascend' ? 'descend' : 'ascend',
    })
  }

  /**
   * Retour à la première page : à appeler dès qu'un filtre change, sinon on
   * reste sur une page 4 qui n'existe plus dans le résultat filtré. Référence
   * stable, pour être utilisable en dépendance d'effet (recherche débouncée).
   */
  const resetPage = useCallback(() => setPage(1), [])

  return {
    page,
    pageSize,
    setPage,
    resetPage,
    sortedInfo,
    handleSort,
    queryArgs: {
      skip: (page - 1) * pageSize,
      take: pageSize,
      orderBy: sortedInfo && returnOrderBy(sortedInfo.field, sortedInfo.order),
    },
  }
}
